import React from 'react';
import { Link } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import usePageMeta from '../hooks/usePageMeta';
import { trackEvent } from '../analytics';
import { OPERATIONS, MOTION_MODES } from '../lib/operationCopy';
import { displayNameOf } from '../lib/operations';
import './MotionTrimPage.css';

// MotionTrim feature landing page (/motiontrim).
//
// Mirrors the MotionPacePage structure: hero, how it works, comparison figure,
// use cases, FAQ (with FAQPage JSON-LD), final CTA.
//
// Gated at BUILD time via REACT_APP_MOTIONPACE_MARKETING — the same switch as
// MotionPacePage. See App.js for the require() / guard pattern.
//
// HONESTY CONSTRAINTS on this page:
//  - No demo video exists for MotionTrim. A comment in the hero marks where a
//    real clip slots in later — no visible placeholder box in the meantime.
//  - No app screenshots. Visual sections use SVG/CSS diagrams only.
//  - No step-by-step instructions for third-party editors.
//  - No GoPro TimeWarp explanations.
//  - Naming comes from operationCopy.js (#379). Never retype "MotionTrim" or
//    "MotionPace" as string literals on this page — import them.
//
// RELATIONSHIP TO MOTIONPACE:
//  MotionTrim and MotionPace are the two modes of the Motion section in the Edits
//  tab. Both measure per-frame motion using the same algorithm. MotionPace
//  varies playback speed so nothing is removed; MotionTrim cuts the still
//  stretches out so the result is shorter. The Edits tab labels them as the
//  trim_stillness and variable_speed operations in the Motion card.
//
//  THIS PAGE DELIBERATELY DOES NOT COMPARE THE TWO (Aaron's call, 2026-09-03).
//  A visitor who landed on /motiontrim wants to know what MotionTrim does, not
//  which sibling to pick — the side-by-side "choose one" callout was removed and
//  the how-it-works copy now stands on its own. The single remaining mention is
//  the cross-sell line in the final CTA's fine print. A test pins that: the how,
//  compare, uses and FAQ sections must not contain MOTIONPACE_NAME.

const PAGE_TITLE = 'MotionTrim — Cut the Still Stretches Out | Tweedia Studio';
const PAGE_DESCRIPTION =
  'MotionTrim reads the motion in your footage frame by frame and removes the still stretches ' +
  'automatically. The result is shorter than the source — only the frames where something ' +
  'was actually happening stay in. No editing required.';
const CANONICAL_PATH = '/motiontrim';

// Canonical names from the naming sources. Never retype these.
const MOTIONTRIM_NAME = MOTION_MODES.trim.name;                // "MotionTrim"
const MOTIONPACE_NAME = OPERATIONS.variable_speed.displayName; // "MotionPace"
const FIX_FLICKER = displayNameOf('deflicker');                // "Fix flicker"

// HOW IT WORKS — three steps, mirroring MotionPacePage's structure.
const HOW_IT_WORKS = [
  {
    id: 'upload',
    title: 'Upload your footage',
    text:
      'A folder of interval photos or a video file. Jobsite cam, GoPro, phone, DSLR, ' +
      'a Pi running overnight — all accepted.',
    art: (
      <svg width="128" height="46" viewBox="0 0 128 46" fill="none" stroke="#00B3A6" strokeWidth="1.5">
        <rect x="16" y="14" width="20" height="15" rx="2" strokeOpacity=".45" />
        <rect x="40" y="14" width="20" height="15" rx="2" strokeOpacity=".65" />
        <rect x="64" y="14" width="20" height="15" rx="2" strokeOpacity=".85" />
        <path d="M96 34h16M104 34V13m0 0-5 6m5-6 5 6" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M14 36h74" stroke="#3a424f" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    id: 'measure',
    title: 'We score motion on every frame',
    text:
      'Each pair of frames gets a score for how much actually changed. Still runs — ' +
      'where the score stays low — are the stretches that will be cut.',
    art: (
      <svg width="128" height="46" viewBox="0 0 128 46" fill="none">
        <g stroke="#5a6572" strokeWidth="3" strokeLinecap="round">
          <path d="M14 34v-6M22 34v-13M30 34v-9M38 34v-19M46 34v-24M54 34v-16M62 34v-6M70 34v-4M78 34v-11M86 34v-21M94 34v-14M102 34v-5M110 34v-8" />
        </g>
        {/* threshold line — still stretches below */}
        <path d="M14 26h100" stroke="#00b3a6" strokeWidth="1.2" strokeDasharray="4 3" />
        <path d="M14 36h100" stroke="#3a424f" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    id: 'cut',
    title: 'Still stretches are cut — not sped through',
    text:
      'Frames below the motion threshold are removed. What remains plays at 1× — no speed ' +
      'changes, no ramps, just the frames where something was happening. The result is shorter.',
    art: (
      <svg width="128" height="46" viewBox="0 0 128 46" fill="none">
        {/* active frames */}
        <g fill="none" stroke="#00B3A6" strokeWidth="1.4" strokeOpacity=".85">
          <rect x="14" y="12" width="9" height="22" rx="1.5" />
          <rect x="24" y="12" width="9" height="22" rx="1.5" />
          <rect x="34" y="12" width="9" height="22" rx="1.5" />
        </g>
        {/* cut mark */}
        <line x1="47" y1="8" x2="47" y2="40" stroke="#00b3a6" strokeWidth="1.2" strokeDasharray="3 2" />
        {/* still frames — greyed */}
        <g fill="none" stroke="#3a424f" strokeWidth="1" strokeOpacity=".45">
          <rect x="49" y="12" width="9" height="22" rx="1.5" />
          <rect x="60" y="12" width="9" height="22" rx="1.5" />
          <rect x="71" y="12" width="9" height="22" rx="1.5" />
        </g>
        {/* cut mark 2 */}
        <line x1="84" y1="8" x2="84" y2="40" stroke="#00b3a6" strokeWidth="1.2" strokeDasharray="3 2" />
        {/* active frames after */}
        <g fill="none" stroke="#00B3A6" strokeWidth="1.4" strokeOpacity=".85">
          <rect x="86" y="12" width="9" height="22" rx="1.5" />
          <rect x="96" y="12" width="9" height="22" rx="1.5" />
          <rect x="106" y="12" width="9" height="22" rx="1.5" />
        </g>
        <path d="M14 40h101" stroke="#3a424f" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    ),
  },
];

// USE CASES
const USE_CASES = [
  {
    id: 'highlight_reel',
    title: 'Highlight reels',
    text:
      'Pull only the frames from a long construction or workshop shoot where something was ' +
      'actually happening. The result is a dense edit — no slow moments, no empty-site filler.',
    icon: <path d="M5 3l14 9-14 9V3z" />,
  },
  {
    id: 'event',
    title: 'Event coverage',
    text:
      'A shoot that covers setup, the event itself, and teardown. MotionTrim drops the empty ' +
      'stretches between them and keeps the action from each phase.',
    icon: (
      <>
        <rect x="3" y="4" width="18" height="14" rx="2" />
        <path d="M8 20h8M12 18v2" />
      </>
    ),
  },
  {
    id: 'workshop',
    title: 'Workshop builds',
    text:
      'A bench camera running all day. The cuts between active work and the long stretch ' +
      'while the glue dries are made automatically — no timeline scrubbing required.',
    icon: <path d="M3 13h18M5 13v7M19 13v7M6.5 13 9 5.5h6L17.5 13M9.8 9h4.4" />,
  },
  {
    id: 'sport',
    title: 'Interval sport and nature',
    text:
      'A bird feeder, a trailhead, a paddock. Intervals capture hours; MotionTrim keeps only ' +
      'the arrivals and leaves and drops everything between.',
    icon: (
      <>
        <circle cx="8" cy="8" r="3" />
        <path d="M20 17H9a3 3 0 1 1 .6-5.9A4 4 0 0 1 17.5 12a2.5 2.5 0 0 1 2.5 5ZM3 21h18" />
      </>
    ),
  },
];

// FAQ — single source for visible content AND FAQPage JSON-LD (see buildJsonLd).
// Keep answers as plain strings: the JSON-LD publishes them verbatim.
const FAQ = [
  {
    q: `Does ${MOTIONTRIM_NAME} speed up my footage?`,
    a:
      'No. Frames below the motion threshold are removed, and everything that remains plays at ' +
      'its original speed. The result is shorter than the source because the still stretches are ' +
      'gone, not because anything was fast-forwarded.',
  },
  {
    q: 'How does it decide what to cut?',
    a:
      'Each pair of adjacent frames gets a motion score based on how much the image changed between ' +
      'them. Runs where the score stays below the threshold are treated as stillness and removed. ' +
      'The threshold is tunable in the Edits tab; the default catches everything a fixed camera ' +
      'captures when nothing in the frame is moving.',
  },
  {
    q: 'Will it cut too much if my scene is slow-moving?',
    a:
      'The motion threshold is a slider in the Edits tab, and the preview updates before you render. ' +
      'If the default setting cuts a slow pan or a gradual change you want to keep, lower the ' +
      'threshold until those frames are back. No credits are spent on previewing.',
  },
  {
    q: 'Does it work with photo sequences and video?',
    a:
      'Both. Drop in a folder of interval photos or a video file — the same sources the rest of ' +
      'Tweedia accepts. Photo sequences are compiled into a continuous clip first, then ' +
      'trimmed. Video files are trimmed directly.',
  },
];

function buildJsonLd() {
  const url = `https://tweediastudio.com${CANONICAL_PATH}`;
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'FAQPage',
        '@id': `${url}#faq`,
        mainEntity: FAQ.map((item) => ({
          '@type': 'Question',
          name: item.q,
          acceptedAnswer: { '@type': 'Answer', text: item.a },
        })),
      },
    ],
  };
}

// The before/after timeline diagram — static SVG/CSS, not a real video.
// Drawn from one segment definition so both rows map the same timeline.
//
// Layout:
//   raw:     [active][active][still][active][still][active][active]
//   trimmed: [active][active]       [active]       [active][active]
//                               ^cut           ^cut
//            A cut mark is drawn only where a still segment was removed, so the
//            figure shows exactly as many cuts as the source had still stretches
//            (2 here). Pairs that were already adjacent — s1|s2, s6|s7 — join
//            with no mark.

// Widths in px, applied inline. Segments are flex-shrink: 0 with no flex-grow,
// so these are fixed sizes, not proportions of the track.
const SEGMENTS = [
  { id: 's1', active: true,  w: 60,  label: 'crew' },
  { id: 's2', active: true,  w: 40,  label: 'crew' },
  { id: 's3', active: false, w: 90,  label: 'still' },
  { id: 's4', active: true,  w: 50,  label: 'work' },
  { id: 's5', active: false, w: 80,  label: 'still' },
  { id: 's6', active: true,  w: 45,  label: 'crew' },
  { id: 's7', active: true,  w: 35,  label: 'crew' },
];

// Drop the still segments and flag each survivor with whether a still stretch
// was cut out immediately before it. A leading still segment trims the head of
// the clip instead of joining two actives, so it produces no mark.
function trimSegments(segments) {
  const kept = [];
  let cutBefore = false;
  segments.forEach((seg) => {
    if (!seg.active) {
      cutBefore = true;
      return;
    }
    kept.push({ ...seg, cutBefore: cutBefore && kept.length > 0 });
    cutBefore = false;
  });
  return kept;
}

const TRIMMED_SEGMENTS = trimSegments(SEGMENTS);

function TimelineSegment({ seg, showLabel }) {
  return (
    <div
      className={`mt-segment ${seg.active ? 'mt-segment-active' : 'mt-segment-still'}`}
      style={{ width: seg.w }}
      title={seg.label}
    >
      {showLabel && seg.active ? seg.label : null}
    </div>
  );
}

function TimelineFigure() {
  return (
    <figure className="mt-figure" data-testid="motiontrim-timeline">
      {/* raw / before */}
      <div className="mt-figure-head">
        <span className="mt-tag mt-tag-grey">Before</span>
        <b>Full capture — active and still stretches</b>
      </div>
      <div className="mt-timeline-wrap">
        <div className="mt-timeline-track" aria-label="Full capture timeline">
          {SEGMENTS.map((seg) => (
            <TimelineSegment key={seg.id} seg={seg} showLabel />
          ))}
        </div>
      </div>

      {/* trimmed / after */}
      <div className="mt-figure-head" style={{ borderTop: '1px solid var(--mt-line)' }}>
        <span className="mt-tag mt-tag-teal">After MotionTrim</span>
        <b>Only the frames where something was moving</b>
      </div>
      <div className="mt-timeline-wrap">
        <div className="mt-timeline-track" aria-label="MotionTrim output timeline">
          {TRIMMED_SEGMENTS.map((seg) => (
            <React.Fragment key={seg.id}>
              {seg.cutBefore && <div className="mt-segment-cut" aria-hidden="true" />}
              <TimelineSegment seg={seg} showLabel />
            </React.Fragment>
          ))}
        </div>
      </div>

      <figcaption className="mt-figure-foot">
        <b>Still stretches (grey) are removed entirely.</b> The active stretches (teal) are joined
        in order — no speed change, no ramp, just a cut. The result is shorter than the source.
        Cut marks show where a still segment was.
      </figcaption>
    </figure>
  );
}

function MotionTrimPage({ user, signIn }) {
  const signedIn = !!user;

  usePageMeta({
    title: PAGE_TITLE,
    description: PAGE_DESCRIPTION,
    canonicalPath: CANONICAL_PATH,
    jsonLd: buildJsonLd(),
  });

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleCtaClick = (ctaId) => () => {
    trackEvent('cta_click', { cta_id: ctaId, signed_in: signedIn });
  };

  const handleSignInCta = (ctaId) => () => {
    trackEvent('cta_click', { cta_id: ctaId, signed_in: false });
    signIn();
  };

  const renderPrimaryCta = (ctaId, label, testId) =>
    signedIn ? (
      <Link
        to="/studio"
        className="mt-cta mt-cta-primary"
        data-testid={testId}
        onClick={handleCtaClick(ctaId)}
      >
        {label}
      </Link>
    ) : (
      <button
        type="button"
        className="mt-cta mt-cta-primary"
        data-testid={testId}
        onClick={handleSignInCta(ctaId)}
      >
        {label}
      </button>
    );

  return (
    <div className="motiontrim-page">
      <Header user={user} signIn={signIn} />
      <main className="mt-main" data-testid="motiontrim-main">

        {/* ---------------- 1 · hero ---------------- */}
        <section className="mt-hero" id="mt-hero" data-testid="motiontrim-hero">
          <div className="mt-hero-glow" aria-hidden="true" />
          <div className="mt-wrap">
            <span className="mt-badge">
              <i className="mt-badge-dot" aria-hidden="true" />
              New in Tweedia Studio
            </span>
            <h1>Cut the still stretches out.</h1>
            <p className="mt-hero-sub">
              {MOTIONTRIM_NAME} reads the motion in your footage frame by frame and removes the
              parts where nothing was moving — automatically. The result is shorter than the source,
              and every frame that made it in earned its place.{' '}
              <b>No keyframes. No scrubbing. No editing.</b>
            </p>
            <div className="mt-hero-ctas">
              {renderPrimaryCta('motiontrim_hero', 'Try MotionTrim free', 'motiontrim-cta-hero')}
              <a
                className="mt-cta mt-cta-secondary"
                href="#mt-compare"
                data-testid="motiontrim-cta-hero-secondary"
                onClick={handleCtaClick('motiontrim_hero_compare')}
              >
                See the difference
              </a>
            </div>
            <p className="mt-hero-note">
              Free plan includes starter credits · no card, no camera subscription
            </p>

            {/*
              Demo video slot — a real clip slots in here later. No placeholder
              screenshot or fabricated app image in the meantime: the page works
              without it, and the comparison figure below carries the argument.
            */}
          </div>
        </section>

        {/* ---------------- 2 · how it works ---------------- */}
        <section className="mt-section" id="mt-how" data-testid="motiontrim-how">
          <div className="mt-wrap">
            <p className="mt-kicker">How it works</p>
            <h2 className="mt-section-title">Three steps. None of them editing.</h2>
            <p className="mt-lede">
              {MOTIONTRIM_NAME} measures motion frame by frame and cuts the still stretches out —
              you never touch a timeline. Upload, preview, render.
            </p>

            <ol className="mt-steps" data-testid="motiontrim-steps">
              {HOW_IT_WORKS.map((step, index) => (
                <li className="mt-step" key={step.id}>
                  <div className="mt-step-art" aria-hidden="true">
                    {step.art}
                  </div>
                  <div className="mt-step-head">
                    <span className="mt-step-num" aria-hidden="true">{index + 1}</span>
                    <h3>{step.title}</h3>
                  </div>
                  <p>{step.text}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* ---------------- 3 · before/after comparison ---------------- */}
        <section
          className="mt-section mt-compare-section"
          id="mt-compare"
          data-testid="motiontrim-compare"
        >
          <div className="mt-wrap">
            <p className="mt-kicker">Before and after</p>
            <h2 className="mt-section-title">Same shoot. Shorter result.</h2>
            <p className="mt-lede">
              The full capture has active stretches and still stretches mixed together.
              MotionTrim removes the still ones. What remains plays at 1× — no speed
              ramps, just the frames where something was happening, in the order they occurred.
            </p>

            <TimelineFigure />

            <p className="mt-explainer">
              Each coloured block is a stretch of footage. Teal blocks are active — crew on site,
              work happening, something changing. Grey blocks are still — camera running, nothing
              in frame moving. MotionTrim keeps the teal and cuts the grey.
            </p>
          </div>
        </section>

        {/* ---------------- 4 · use cases ---------------- */}
        <section
          className="mt-section mt-uses-section"
          id="mt-uses"
          data-testid="motiontrim-uses"
        >
          <div className="mt-wrap">
            <p className="mt-kicker">Where it earns its keep</p>
            <h2 className="mt-section-title">Any shoot where the interesting part is a fraction of the whole</h2>
            <p className="mt-lede">
              A fixed camera runs all day. MotionTrim keeps only the part that matters.
            </p>

            <div className="mt-uses">
              {USE_CASES.map((useCase) => (
                <div className="mt-use" key={useCase.id}>
                  <div className="mt-use-ico" aria-hidden="true">
                    <svg
                      width="22"
                      height="22"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#00B3A6"
                      strokeWidth="1.6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      {useCase.icon}
                    </svg>
                  </div>
                  <h3>{useCase.title}</h3>
                  <p>{useCase.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ---------------- 5 · FAQ ---------------- */}
        <section className="mt-section mt-faq-section" id="mt-faq" data-testid="motiontrim-faq">
          <div className="mt-wrap">
            <h2 className="mt-section-title">Questions people actually ask</h2>
            <div className="mt-faq" style={{ marginTop: '32px' }}>
              {FAQ.map((item) => (
                <div className="mt-faq-item" key={item.q}>
                  <h3>{item.q}</h3>
                  <p>{item.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ---------------- 6 · final CTA ---------------- */}
        <div className="mt-wrap">
          <section className="mt-final" id="mt-start" data-testid="motiontrim-final-cta">
            <h2>Let the motion decide what stays.</h2>
            <p>
              Upload a shoot, flip one switch, and preview the trimmed result before you spend a
              credit.
            </p>
            <div className="mt-final-row">
              {renderPrimaryCta('motiontrim_final', 'Try MotionTrim free', 'motiontrim-cta-final')}
              <Link
                to="/features"
                className="mt-cta mt-cta-secondary"
                data-testid="motiontrim-cta-final-secondary"
                onClick={handleCtaClick('features_from_motiontrim')}
              >
                See all features
              </Link>
            </div>
            <p className="mt-fine">
              Previewing is always free. Credits are charged on a finished render, and a failed
              render refunds automatically. Also available: {FIX_FLICKER} and {MOTIONPACE_NAME}.
            </p>
          </section>
        </div>

      </main>
      <Footer />
    </div>
  );
}

export default MotionTrimPage;
export {
  FAQ,
  HOW_IT_WORKS,
  USE_CASES,
  MOTIONTRIM_NAME,
  MOTIONPACE_NAME,
  buildJsonLd,
  SEGMENTS,
  trimSegments,
};
